<template>
  <Container :class="$style.container">
    <ButtonBack
      text="Dashboard"
      :to="$options.homeRoute"
    />
    <Container
      :class="$style.innerContainer"
      data-test-id="ViewAll__container"
    >
      <div :class="$style.headerStyle">
        <HeadingMedium>
          {{ displayName }}
        </HeadingMedium>
        <Icon
          v-if="tableData.length"
          :class="iconColor"
          :icon="widgetIcon"
          :scale="2"
        />
      </div>
      <ItemListPagination
        :columns="tableColumns"
        :currentPageNum="currentPageNum"
        :indeterminateSelectAll="indeterminateSelectAll"
        :itemComponent="$options.RowData"
        :loading="isListLoading"
        :rowsPerPage="10"
        :showActions="showActions"
        :sortBy="sortBy"
        :sortDirection="sortDirection"
        :tableData="tableData"
        :totalCount="totalCount"
        @pageChange="handlePageChange"
        @sortColumn="handleSortColumn"
      >
        <template v-slot:toolbar>
          <HomeSearchBar
            :count="totalCount"
            :disabled="!showActions"
            searchPlaceholder="Search"
            :shouldFocusSearch="shouldFocusSearch"
            :toolBarDisplayName="toolBarDisplayName"
            @triggerSearch="handleEnterSearchTerm"
          />
        </template>
      </ItemListPagination>
    </Container>
  </Container>
</template>
<script>
import {
  ButtonBack,
  Container,
  HeadingMedium,
  Icon,
  ItemListPagination,
  Types,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, ItemTypeRoutes } from 'client/util/Constants';
import { ReportSortFields, ReportStatusConsts, ReportTypeConsts } from 'Reports/constants/reportsConstants';
import {
  eventProperties,
} from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapState } from 'vuex';
import DateUtil from 'client/util/DateUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import HomeSearchBar from 'client/HomePageV2/components/HomeSearchBar.vue';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import InvestigativePageMixin from 'client/HomePageV2/mixins/InvestigativePageMixin';
import ReportsApi from 'client/api/v2/ReportsApi';
import RowData from 'client/HomePageV2/components/RowData.vue';
import ViewAllMixin from 'client/HomePageV2/mixins/ViewAllMixin';

const { Column } = Types;
const {
  widgetActions,
  widgetNames,
  widgetTableColumns,
} = HomeWidgetConsts;

const {
  description,
  displayLogic,
  location,
  section,
  text: textEventProp,
} = eventProperties;

const administratorModel = 'AdministratorModel';

export default {
  name: 'ViewAllOthers',
  components: {
    ButtonBack,
    Container,
    HeadingMedium,
    HomeSearchBar,
    Icon,
    ItemListPagination,
  },

  homeRoute: ItemTypeRoutes[ItemTypeConsts.home],

  mixins: [
    InvestigativePageMixin,
    ViewAllMixin,
  ],

  data() {
    return {
      activeTab: 'All',
      showActions: false,
      sortByColumn: '',
      widgetName: this.$route.params.widgetName,
    };
  },

  RowData,
  computed: {
    ...mapState(administratorModel, [
      'resetKey',
    ]),
    displayName() {
      return widgetNames[this.widgetName];
    },

    iconColor() {
      if (HomeWidgetConsts.widgetIcons[this.widgetName] === 'error' && !this.totalCount) { return this.$style.colorSuccess; }
      if (HomeWidgetConsts.widgetIcons[this.widgetName] === 'error' && this.totalCount > 0) { return this.$style.colorWarning; }
      return this.$style.colorInfo;
    },

    tableColumns() {
      return widgetTableColumns[this.widgetName].map(column => new Column(column));
    },

    widgetIcon() {
      if (HomeWidgetConsts.widgetIcons[this.widgetName] === 'error' && !this.totalCount) { return 'checkmarkCircle'; }
      return HomeWidgetConsts.widgetIcons[this.widgetName];
    },

  },
  watch: {
    loading(currentValue) {
      this.isListLoading = currentValue;
    },

    resetKey: {
      handler() {
        this.handleCreated();
      },
    },
  },

  created() {
    this.handleCreated();
  },

  methods: {

    ...mapActions(administratorModel, ['resetActionKey']),

    async handleCreated() {
      if (this.widgetName === 'reportsReadyForDownload') {
        await this.handleReportsForDownload();
      }

      let start = this.currentPageNum * this.rowsPerPage;
      let end = start + this.rowsPerPage;
      this.tableData = this.data.slice(start, end);
      if (!this.tableData.length) {
        if (this.data.length) {
          start = (this.currentPageNum - 1) * this.rowsPerPage;
          end = start + this.rowsPerPage;
          this.currentPageNum -= 1;
          this.tableData = this.data.slice(start, end);
        } else {
          this.currentPageNum = 0;
        }
      }
      this.handleSortColumn();
    },

    handleEvent(text) {
      EventHelper.triggerButtonClicked({
        [description]: `Click ${text} action on investigation page`,
        [displayLogic]: 'Visible if investigation has actions',
        [location]: 'Investigation Page',
        [section]: 'Investigative Pages for Widgets',
        [textEventProp]: text,
      });
    },

    /** **
     *
     * Reports ready for download
     *
     */
    async handleReportsForDownload() {
      this.sortBy = ReportSortFields.created_at;
      this.isListLoading = true;
      return ReportsApi.fetchReportsList({ sort: this.sortBy, skip: 0, limit: 10 }).then(
        ({ data }) => {
          if (data.length > 0) {
            this.data = data
              .filter(rep => rep.status === ReportStatusConsts.completed);
            this.totalCount = this.data.length;
            this.data = this.data.map((rep) => ({
              id: rep.id,
              type: ReportTypeConsts[rep.type],
              requester_email: rep.requester_email,
              created_at: DateUtil.formatDateTime(rep.created_at),
              expiration: DateUtil.formatDateTime(rep.expiration),
              artifacts: rep.artifacts,
              status: rep.status,
              actions: widgetActions[this.widgetName],
            }));
          }
        },
      )
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isListLoading = false;
        });
    },
  },
};
</script>

<style module>
.container {
  background-color: var(--jc-color-disabled);
  display: inherit;
  height: 100%;
  padding: var(--jc-spacer-x-small);
}

.innerContainer {
  background-color: var(--jc-color-surface-light);
  margin-top: var(--jc-spacer);
  width: 100%;
}

.headerStyle {
  display: flex;
  width: 90%;
}

.list {
  padding-left: var(--jc-spacer);
}

.colorWarning {
  color: var(--jc-color-warning-strong);
  padding-left: var(--jc-spacer-small);
  padding-top: var(--jc-spacer-x-small);
}

.colorSuccess {
  color: var(--jc-color-success-base);
  padding-left: var(--jc-spacer-small);
  padding-top: var(--jc-spacer-x-small);
}

.colorInfo {
  color: var(--jc-color-informational-base);
  padding-left: var(--jc-spacer-small);
  padding-top: var(--jc-spacer-x-small);
}

.dropdownClass {
  float: right;
}
</style>
